import { useEffect, useState, useRef } from 'react';
import { X, Loader2, Calendar, User, FileText, Package, AlertCircle, Download, Eye, Upload, Clock, Truck, ExternalLink } from 'lucide-react';
import { purchasingAPI, PurchaseOrder, POChangeLog, PODocument } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import CommunicationLog from './CommunicationLog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import VerticalTimeline from './timeline/VerticalTimeline';
import { useFormatters } from '../../contexts/PreferencesContext';
import { toast } from 'sonner';
import {
  getAcknowledgeStatusBadge,
  getLineStatusBadge,
  getInspectionStatusBadge,
  formatFileSize,
  getDocumentTypeLabel,
  getDocumentTypeColor,
  groupDocumentsByType,
  convertChangelogsToTimeline,
} from './PODetailHelpers';
import { PrintablePOSummary } from './PrintablePOSummary';

interface PODetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  poId: number;
}

export default function PODetailModal({ isOpen, onClose, poId }: PODetailModalProps) {
  const { user } = useAuth();
  const isSupplierUser = user?.platform === 'SUPPLIER';
  const [po, setPO] = useState<PurchaseOrder | null>(null);
  const [changelogs, setChangelogs] = useState<POChangeLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && poId) {
      fetchPODetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, poId]);

  const fetchPODetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const [poResponse, changelogResponse] = await Promise.all([
        purchasingAPI.getOrder(poId),
        purchasingAPI.getChangelogs({ po: poId, ordering: '-change_date' }),
      ]);

      setPO(poResponse.data);
      setChangelogs(changelogResponse.data.results);
    } catch (err: unknown) {
      console.error('Failed to fetch PO details:', err);
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Failed to load PO details');
    } finally {
      setLoading(false);
    }
  };

  const { formatCurrency: contextFormatCurrency, formatDate: contextFormatDate, formatDateTime: contextFormatDateTime } = useFormatters();

  // Wrapper to handle string values from API
  const formatCurrency = (value: string, currency?: string, decimals?: boolean | number) => {
    const num = parseFloat(value);
    return contextFormatCurrency(num, currency, decimals);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return contextFormatDate(dateString);
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return contextFormatDateTime(dateString);
  };

  const handleDocumentDownload = async (doc: PODocument) => {
    try {
      const response = await purchasingAPI.downloadPODocument(doc.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = doc.file_name || 'document';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${doc.file_name}`);
    } catch (err) {
      console.error('Download failed:', err);
      toast.error(`Failed to download ${doc.file_name}`);
    }
  };

  const handleDocumentView = (doc: PODocument) => {
    const viewUrl = doc.file_url || doc.file || `/api/purchasing/documents/${doc.id}/download/`;
    window.open(viewUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDocumentUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !po) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('po', String(po.id));
    formData.append('document_type', 'other');
    formData.append('source', 'supplier');

    try {
      await purchasingAPI.uploadPODocument(formData);
      toast.success(`Uploaded ${file.name}`);
      fetchPODetails();
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(`Failed to upload ${file.name}`);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      {/* Hidden file input for document upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelected}
        className="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.csv"
      />
      <div className="bg-white rounded-lg shadow-xl w-full max-h-[95vh] flex flex-col" style={{ maxWidth: '1400px' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-semibold">Purchase Order Details</h2>
            {po && (
              <p className="text-sm text-muted-foreground mt-1">
                {po.po_number} - {po.supplier_name}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">Error: {error}</p>
              <button
                onClick={fetchPODetails}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Try again
              </button>
            </div>
          ) : po ? (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="lines">Lines ({po.lines?.length || 0})</TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
                <TabsTrigger value="communication">Communication</TabsTrigger>
                <TabsTrigger value="history">History ({changelogs.length})</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6 mt-6">
                {/* Header Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">PO Number</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{po.po_number}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        po.status === 'closed'
                          ? 'bg-slate-100 text-slate-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {po.status === 'closed' ? 'Closed' : 'Open'}
                      </span>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Total Value</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{formatCurrency(po.total_value, po.currency, 3)}</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Company Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Customer Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Customer</label>
                        <div className="font-medium">{po.customer_name || 'N/A'}</div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Company</label>
                        <div className="font-medium">{po.customer_name}</div>
                      </div>
                      {po.delivery_warehouse_details && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Warehouse</label>
                          <div className="font-medium">{po.delivery_warehouse_details.name}</div>
                        </div>
                      )}
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Created By</label>
                        <div className="font-medium">{po.created_by_name || 'N/A'}</div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Supplier Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Company</label>
                        <div className="font-medium">{po.supplier_name}</div>
                      </div>
                      {po.supplier_group_name && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Supplier Group</label>
                          <div className="font-medium">{po.supplier_group_name}</div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Dates and Terms */}
                <Card>
                  <CardHeader>
                    <CardTitle>Dates & Terms</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Issue Date
                        </label>
                        <div className="font-medium mt-1">{formatDate(po.issue_date)}</div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Currency</label>
                        <div className="font-medium mt-1">{po.currency}</div>
                      </div>
                    </div>
                    {po.payment_terms && (
                      <div className="mt-4">
                        <label className="text-xs font-medium text-muted-foreground">Payment Terms</label>
                        <div className="font-medium mt-1">{po.payment_terms}</div>
                      </div>
                    )}
                    {po.notes && (
                      <div className="mt-4">
                        <label className="text-xs font-medium text-muted-foreground">Notes</label>
                        <div className="text-sm mt-1 p-3 bg-muted rounded-lg">{po.notes}</div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Lines Tab */}
              <TabsContent value="lines" className="mt-6">
                {po.lines && po.lines.length > 0 ? (
                  <div className="space-y-4">
                    {po.lines.map((line) => (
                      <Card key={line.id}>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-sm font-bold">
                                  {line.line_number}
                                </span>
                                <a
                                  href={`/purchasing?poFilter=${encodeURIComponent(po.po_number)}&lineFilter=${line.line_number}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group cursor-pointer hover:bg-blue-50 rounded-lg px-2 py-1 -ml-2 transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="flex items-center gap-2">
                                    <div>
                                      <div className="font-mono font-semibold group-hover:text-blue-700 transition-colors">{line.part_number}</div>
                                      <div className="text-sm text-muted-foreground">{line.description}</div>
                                    </div>
                                    <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                </a>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {line.on_hold && (
                                <div className="flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                  <AlertCircle className="h-3 w-3" />
                                  On Hold
                                </div>
                              )}
                              {getLineStatusBadge(line.status)}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Quantity</label>
                              <div className="font-medium mt-1">
                                {parseFloat(line.quantity).toFixed(0)} {line.unit_of_measure}
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Unit Price</label>
                              <div className="font-medium mt-1">{formatCurrency(line.unit_price, po.currency, 3)}</div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Total Price</label>
                              <div className="font-medium mt-1">{formatCurrency(line.total_price, po.currency, 3)}</div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Customer Required Date</label>
                              <div className="font-medium mt-1">{formatDate(line.delivery_date)}</div>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Received</label>
                              <div className="font-medium mt-1 text-green-700">
                                {parseFloat(line.received_qty).toFixed(0)}
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Outstanding</label>
                              <div className="font-medium mt-1 text-orange-700">
                                {parseFloat(line.outstanding_qty).toFixed(0)}
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Ack Status</label>
                              <div className="mt-1">{getAcknowledgeStatusBadge(line.acknowledge_status)}</div>
                            </div>
                            {line.supplier_promise_date && (
                              <div>
                                <label className="text-xs font-medium text-muted-foreground">Promise Date</label>
                                <div className="font-medium mt-1">
                                  {formatDate(line.supplier_promise_date)}
                                  {line.original_promise_date &&
                                   line.supplier_promise_date !== line.original_promise_date && (
                                    <div className="text-xs text-orange-600">
                                      (was {formatDate(line.original_promise_date)})
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* CR-4716: Buyer ID hidden from supplier users */}
                          {!isSupplierUser && line.buyer_id && (
                            <div className="mt-4 pt-4 border-t">
                              <label className="text-xs font-medium text-muted-foreground">Buyer ID</label>
                              <div className="font-medium mt-1">{line.buyer_id}</div>
                            </div>
                          )}

                          {/* GRNs Section */}
                          {line.grns && line.grns.length > 0 && (
                            <div className="mt-4 pt-4 border-t">
                              <div className="flex items-center gap-2 mb-3">
                                <Truck className="h-4 w-4 text-muted-foreground" />
                                <label className="text-sm font-medium">Goods Receipt Notes ({line.grns.length})</label>
                              </div>
                              <div className="space-y-2">
                                {line.grns.map((grn) => (
                                  <div
                                    key={grn.id}
                                    className="bg-muted/50 rounded-lg p-3 text-sm"
                                  >
                                    <div className="flex items-start justify-between gap-4">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="font-mono font-medium text-primary">{grn.grn_number}</span>
                                          {getInspectionStatusBadge(grn.inspection_status)}
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                          <div>
                                            <span className="font-medium">Received:</span>{' '}
                                            {formatDate(grn.receipt_date)}
                                          </div>
                                          <div>
                                            <span className="font-medium">Qty:</span>{' '}
                                            <span className="text-foreground">{parseFloat(grn.quantity_received).toFixed(0)}</span>
                                            {parseFloat(grn.quantity_rejected) > 0 && (
                                              <span className="text-red-600 ml-1">
                                                (-{parseFloat(grn.quantity_rejected).toFixed(0)} rejected)
                                              </span>
                                            )}
                                          </div>
                                          {grn.batch_number && (
                                            <div>
                                              <span className="font-medium">Batch:</span>{' '}
                                              <span className="text-foreground">{grn.batch_number}</span>
                                            </div>
                                          )}
                                          {grn.packing_slip_number && (
                                            <div>
                                              <span className="font-medium">Packing Slip:</span>{' '}
                                              <span className="text-foreground">{grn.packing_slip_number}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No lines available for this purchase order</p>
                  </div>
                )}
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="mt-6">
                {po.documents && po.documents.length > 0 ? (
                  <div className="space-y-6">
                    {/* Upload Section */}
                    <Card>
                      <CardContent className="pt-6">
                        <button
                          onClick={handleDocumentUpload}
                          className="w-full border-2 border-dashed border-border rounded-lg p-6 hover:border-primary hover:bg-primary/5 transition-colors flex flex-col items-center gap-2"
                        >
                          <Upload className="h-8 w-8 text-muted-foreground" />
                          <div className="text-sm font-medium">Upload New Document</div>
                          <div className="text-xs text-muted-foreground">
                            Click to upload or drag and drop
                          </div>
                        </button>
                      </CardContent>
                    </Card>

                    {/* Documents grouped by type */}
                    {Object.entries(groupDocumentsByType(po.documents)).map(([type, docs]) => (
                      <Card key={type}>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="h-5 w-5 text-muted-foreground" />
                              <div>
                                <CardTitle className="text-base">{getDocumentTypeLabel(type)}</CardTitle>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {docs.length} {docs.length === 1 ? 'version' : 'versions'} available
                                </p>
                              </div>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDocumentTypeColor(type)}`}>
                              {type}
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {/* Version History */}
                          <div className="space-y-3">
                            {docs.map((doc, index) => (
                              <div
                                key={doc.id}
                                className={`border rounded-lg p-4 ${index === 0 ? 'border-primary bg-primary/5' : 'border-border'}`}
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2">
                                      <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                      <div className="font-medium text-sm truncate">
                                        {doc.file_name || 'Untitled Document'}
                                      </div>
                                      {index === 0 && (
                                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                                          Current
                                        </span>
                                      )}
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-muted-foreground">
                                      <div className="flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        <span>{formatDateTime(doc.upload_date)}</span>
                                      </div>
                                      {doc.uploaded_by_details && (
                                        <div className="flex items-center gap-1">
                                          <User className="h-3 w-3" />
                                          <span>{doc.uploaded_by_details.full_name}</span>
                                        </div>
                                      )}
                                      <div className="flex items-center gap-1">
                                        <Package className="h-3 w-3" />
                                        <span>{formatFileSize(doc.file_size)}</span>
                                      </div>
                                      <div className="text-muted-foreground">
                                        Version {docs.length - index}
                                      </div>
                                    </div>

                                    {doc.description && (
                                      <div className="mt-2 text-sm text-muted-foreground">
                                        {doc.description}
                                      </div>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                      onClick={() => handleDocumentView(doc)}
                                      className="p-2 hover:bg-primary/10 rounded transition-colors"
                                      title="View document"
                                    >
                                      <Eye className="h-4 w-4 text-primary" />
                                    </button>
                                    <button
                                      onClick={() => handleDocumentDownload(doc)}
                                      className="p-2 hover:bg-primary/10 rounded transition-colors"
                                      title="Download document"
                                    >
                                      <Download className="h-4 w-4 text-primary" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Upload Section - Empty State */}
                    <Card>
                      <CardContent className="pt-6">
                        <button
                          onClick={handleDocumentUpload}
                          className="w-full border-2 border-dashed border-border rounded-lg p-8 hover:border-primary hover:bg-primary/5 transition-colors flex flex-col items-center gap-3"
                        >
                          <Upload className="h-12 w-12 text-muted-foreground" />
                          <div className="text-sm font-medium">Upload Document</div>
                          <div className="text-xs text-muted-foreground text-center">
                            Click to upload or drag and drop<br />
                            PDF, Images, or other document types
                          </div>
                        </button>
                      </CardContent>
                    </Card>

                    {/* Empty State */}
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No documents attached to this purchase order</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Upload documents such as purchase orders, certificates, or specifications
                      </p>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* History Tab */}
              <TabsContent value="communication" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Communication</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {po.lines && po.lines.length > 0 ? (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground mb-4">
                          Select a PO line to view its communication thread.
                        </p>
                        {po.lines.map((line) => (
                          <details key={line.id} className="border rounded-lg">
                            <summary className="p-3 cursor-pointer hover:bg-muted/30 font-medium text-sm">
                              Line {line.line_number} - {line.part_number || 'N/A'} ({line.description || 'No description'})
                            </summary>
                            <div className="p-3 border-t">
                              <CommunicationLog
                                lineId={line.id}
                                poId={po.id}
                                partNumber={line.part_number}
                              />
                            </div>
                          </details>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No PO lines available for communication.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="history" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Audit History Timeline</CardTitle>
                    <p className="text-sm text-muted-foreground">Track all changes and events for this purchase order</p>
                  </CardHeader>
                  <CardContent>
                    <VerticalTimeline items={convertChangelogsToTimeline(changelogs)} />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          ) : null}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border rounded-lg hover:bg-muted"
          >
            Close
          </button>
          <button
            onClick={() => window.print()}
            disabled={!po}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Print
          </button>
        </div>
      </div>

      {/* Print-only summary — hidden on screen via `hidden print:block`,
          revealed by @media print in index.css which also hides every
          other DOM surface. window.print() therefore renders just this
          subtree on a clean A4 page. */}
      {po && (
        <PrintablePOSummary
          po={po}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          formatDateTime={formatDateTime}
          generatedBy={user?.email || null}
        />
      )}
    </div>
  );
}
